import {
  Calendar,
  User,
  ArrowRight,
  Search,
  Loader2,
  Share2,
  Facebook,
  Instagram,
  Linkedin,
  Link2,
  Video,
} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
// import { ImageWithFallback } from "../figma/ImageWithFallback";
import { notify } from "@/components/common/Notification";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { SitePagination } from "../ui/SitePagination";
import {
  blogsAPI,
  type BlogDetailItem,
  type BlogListItem,
} from "../../services/api";

const ITEMS_PER_PAGE = 6;

function formatBlogDate(dateStr: string | null): string {
  if (!dateStr) {
    return "—";
  }
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function mapApiBlogToPost(blog: BlogListItem) {
  const excerpt = blog.description?.trim() ?? blog.sub_title?.trim() ?? "";
  return {
    id: blog.id,
    title: blog.title,
    excerpt: excerpt ? excerpt : "No description.",
    category: blog.category_name ?? "Uncategorized",
    author: "JBF",
    date: formatBlogDate(blog.date),
    readTime: "—",
    image: blog.banner ?? "",
    video: blog.video ?? "",
  };
}

export default function BlogsPage() {
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [blogPosts, setBlogPosts] = useState<
    ReturnType<typeof mapApiBlogToPost>[]
  >([]);
  const [categories, setCategories] = useState<string[]>(["All"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [blogDetail, setBlogDetail] = useState<BlogDetailItem | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory =
      selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPosts.length / ITEMS_PER_PAGE)
  );
  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPosts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPosts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery]);

  // Load initial blogs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await blogsAPI.getBlogList();
        if (cancelled) {
          return;
        }
        const enabled = data.filter(b => b.is_enabled);
        const mapped = enabled.map(b => mapApiBlogToPost(b));
        setBlogPosts(mapped);
        const categoryNames = [
          "All",
          ...Array.from(
            new Set(
              enabled.map(b => b.category_name).filter(Boolean) as string[]
            )
          ).sort(),
        ];
        setCategories(categoryNames);
      } catch {
        if (!cancelled) {
          setError("Failed to load blogs.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openBlogDetail = async (blogId: number) => {
    setDetailModalOpen(true);
    setBlogDetail(null);
    setDetailError(null);
    setLoadingDetail(true);
    try {
      const detail = await blogsAPI.getBlogDetail(String(blogId));
      setBlogDetail(detail);
    } catch {
      setDetailError("Failed to load blog details.");
    } finally {
      setLoadingDetail(false);
    }
  };

  // Handle URL ID for deep linking
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const blogId = params.get("id");
    if (blogId && !blogDetail) {
      void openBlogDetail(Number(blogId));
    }
  }, [location.search, blogDetail]);

  const shareBlog = (platform: string, blog: BlogDetailItem) => {
    const url =
      window.location.origin + window.location.pathname + "?id=" + blog.id;
    const dateStr = formatBlogDate(blog.date);
    const categoryStr = blog.category_name ?? "Uncategorized";

    // Create a rich text description with ALL details
    const shareTitle = `📢 *${blog.title}*`;
    const shareSubtitle = blog.sub_title ? `\n_${blog.sub_title}_` : "";
    const shareMeta = `\n\n📂 *Category:* ${categoryStr}\n🗓️ *Date:* ${dateStr}`;
    const shareBanner = blog.banner ? `\n🖼️ *Banner:* ${blog.banner}` : "";
    const shareDesc = blog.description
      ? `\n\n📝 *Description:*\n${blog.description}`
      : "";

    const fullMessage = `${shareTitle}${shareSubtitle}${shareMeta}${shareBanner}${shareDesc}\n\n🔗 *Read more at:* ${url}`;

    let shareUrl = "";
    switch (platform) {
      case "facebook":
        // Facebook sharer primarily uses the URL and scrapes OG tags
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullMessage)}`;
        break;
      case "instagram":
        shareUrl = `https://www.instagram.com/sharer/sharer.php?u=${encodeURIComponent(fullMessage)}`;
        return;
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullMessage)}`;
        break;
      case "whatsapp":
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(fullMessage)}`;
        break;
      case "copy":
        void navigator.clipboard.writeText(url);
        notify.success("Link copied to clipboard!");
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank", "width=600,height=400");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#7A1E1E] to-[#5A1616] py-20 text-white">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-4xl lg:text-5xl">JBF Blogs</h1>
            <p className="text-xl text-gray-200">
              Insights, stories, and updates from the Jain Boarding Federation
              community
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="top-[152px] z-40 border-b border-gray-100 bg-white">
        <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            {/* Search Bar */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
              <input
                type="text"
                placeholder="Search blogs..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#D4A017]"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`rounded-full px-4 py-2 text-sm transition-all ${
                    selectedCategory === category
                      ? "bg-[#7A1E1E] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Blog Post */}
      {/* {featuredPost && selectedCategory === "All" && !searchQuery && (
        <section className="bg-gradient-to-br from-[#FFF9F0] to-white py-16">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <div className="inline-block rounded-full bg-[#D4A017] px-4 py-2 text-sm text-white">
                Featured Article
              </div>
            </div>

            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div className="group relative h-[400px] overflow-hidden rounded-2xl">
                <ImageWithFallback
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>

              <div>
                <div className="mb-4 flex items-center gap-4">
                  <span className="rounded-full bg-[#7A1E1E]/10 px-3 py-1 text-sm text-[#7A1E1E]">
                    {featuredPost.category}
                  </span>
                  <span className="text-sm text-gray-500">
                    {featuredPost.readTime}
                  </span>
                </div>

                <h2 className="mb-4 text-3xl text-gray-900 lg:text-4xl">
                  {featuredPost.title}
                </h2>

                <p className="mb-6 text-lg text-gray-600">
                  {featuredPost.excerpt}
                </p>

                <div className="mb-8 flex items-center gap-6 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>{featuredPost.author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{featuredPost.date}</span>
                  </div>
                </div>

                <button className="group inline-flex items-center gap-2 rounded-lg bg-[#7A1E1E] px-6 py-3 text-white transition-all hover:bg-[#5A1616]">
                  Read Full Article
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )} */}

      {/* Blog Grid */}
      <section className="py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-10 w-10 animate-spin text-[#7A1E1E]" />
            </div>
          ) : error ? (
            <div className="py-20 text-center">
              <p className="text-lg text-red-600">{error}</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-lg text-gray-500">
                No blogs found matching your criteria.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-2xl text-gray-900">
                  {selectedCategory === "All"
                    ? "Latest Articles"
                    : `${selectedCategory} Articles`}
                </h2>
              </div>

              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {paginatedPosts.map(post => (
                  <article
                    key={post.id}
                    className="group overflow-hidden rounded-xl border border-gray-100 bg-white transition-all duration-300 hover:shadow-lg"
                  >
                    <div className="relative h-56 overflow-hidden bg-black">
                      {post.video ? (
                        <div className="relative h-full w-full">
                          {(() => {
                            const videoUrl = post.video;
                            let videoId = "";
                            if (videoUrl.includes("youtu.be/")) {
                              videoId = videoUrl
                                .split("youtu.be/")[1]
                                ?.split("?")[0];
                            } else if (videoUrl.includes("v=")) {
                              videoId = videoUrl.split("v=")[1]?.split("&")[0];
                            } else if (videoUrl.includes("embed/")) {
                              videoId = videoUrl
                                .split("embed/")[1]
                                ?.split("?")[0];
                            }

                            if (videoId) {
                              return (
                                <img
                                  src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                                  alt={post.title}
                                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                  onError={e => {
                                    (e.target as HTMLImageElement).src =
                                      `https://img.youtube.com/vi/${videoId}/0.jpg`;
                                  }}
                                />
                              );
                            }
                            return (
                              <div className="flex h-full items-center justify-center text-sm text-white">
                                No Video
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center bg-gray-100 text-gray-400">
                          <Video className="h-12 w-12" />
                          <span className="mt-2 text-xs">
                            Video not available
                          </span>
                        </div>
                        /* <ImageWithFallback
                          src={post.image}
                          alt={post.title}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        /> */
                      )}
                      <div className="absolute left-4 top-4">
                        <span className="rounded-full bg-white/90 px-3 py-1 text-sm text-[#7A1E1E] backdrop-blur-sm">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="mb-3 flex items-center gap-4 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>{post.date}</span>
                        </div>
                        {/* <span>•</span>
                        <span>{post.readTime}</span> */}
                      </div>

                      <h3 className="mb-3 line-clamp-2 text-xl text-gray-900 transition-colors group-hover:text-[#7A1E1E]">
                        {post.title}
                      </h3>

                      <p className="mb-4 line-clamp-3 text-gray-600">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <User className="h-4 w-4" />
                          <span>{post.author}</span>
                        </div>

                        <button
                          type="button"
                          onClick={() => openBlogDetail(post.id)}
                          className="group flex items-center gap-1 text-sm text-[#7A1E1E] transition-colors hover:text-[#5A1616]"
                        >
                          Read More
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              <div className="mt-10">
                <SitePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={filteredPosts.length}
                  pageSize={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPage}
                  itemLabel="blogs"
                />
              </div>
            </>
          )}
        </div>
      </section>

      {/* Blog detail modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden border-gray-200 bg-white p-0 shadow-xl [&>button]:right-4 [&>button]:top-4 [&>button]:flex [&>button]:h-10 [&>button]:w-10 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-md [&>button]:bg-gray-200 [&>button]:text-gray-700 [&>button]:shadow-sm [&>button]:hover:bg-gray-300">
          <DialogHeader className="sr-only">
            <DialogTitle>Blog details</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-10 w-10 animate-spin text-[#7A1E1E]" />
              </div>
            ) : detailError ? (
              <div className="px-6 py-12 text-center">
                <p className="text-red-600">{detailError}</p>
              </div>
            ) : blogDetail ? (
              <>
                <div className="relative aspect-video w-full overflow-hidden bg-black sm:aspect-video">
                  {blogDetail.video ? (
                    <div className="relative h-full w-full">
                      {(() => {
                        const videoUrl = blogDetail.video;
                        let videoId = "";
                        if (videoUrl.includes("youtu.be/")) {
                          videoId = videoUrl
                            .split("youtu.be/")[1]
                            ?.split("?")[0];
                        } else if (videoUrl.includes("v=")) {
                          videoId = videoUrl.split("v=")[1]?.split("&")[0];
                        } else if (videoUrl.includes("embed/")) {
                          videoId = videoUrl.split("embed/")[1]?.split("?")[0];
                        }

                        if (videoId) {
                          return (
                            <iframe
                              className="h-full w-full"
                              src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                              title="YouTube video player"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          );
                        }
                        return (
                          <div className="flex h-full items-center justify-center text-sm text-white">
                            Invalid Video URL
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center bg-gray-200 text-gray-500">
                      <Video className="h-16 w-16" />
                      <span className="mt-2 font-medium">
                        Video link not provided
                      </span>
                    </div>
                    /* <ImageWithFallback
                      src={blogDetail.banner ?? ""}
                      alt={blogDetail.title}
                      className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                    /> */
                  )}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <span className="mb-3 inline-block rounded-full bg-[#D4A017] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">
                      {blogDetail.category_name ?? "Uncategorized"}
                    </span>
                    <h2 className="text-2xl font-bold leading-tight text-white sm:text-3xl">
                      {blogDetail.title}
                    </h2>
                  </div>
                </div>

                <div className="p-6 sm:p-8">
                  <div className="mb-8 flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-6 text-sm text-gray-500">
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#7A1E1E]/10 text-[#7A1E1E]">
                          <User className="h-4 w-4" />
                        </div>
                        <span className="font-medium text-gray-900">JBF</span>
                      </div>
                      <div className="flex items-center gap-2 border-l border-gray-200 pl-6">
                        <Calendar className="h-4 w-4" />
                        <span>{formatBlogDate(blogDetail.date)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="prose prose-sm max-w-none space-y-6">
                    {blogDetail.sub_title && (
                      <h3 className="text-xl font-semibold leading-relaxed text-gray-800">
                        {blogDetail.sub_title}
                      </h3>
                    )}
                    <div className="space-y-4 leading-relaxed text-gray-700">
                      {blogDetail.description
                        ?.split("\n")
                        .map((para: string, i: number) => (
                          <p
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            className="whitespace-pre-wrap"
                          >
                            {para}
                          </p>
                        )) ?? "No description available."}
                    </div>
                  </div>

                  <div className="mt-10 space-y-4 border-t border-gray-100 pt-4">
                    <p className="text-center text-sm font-semibold uppercase tracking-wider text-gray-500">
                      Share this blog
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                      <button
                        onClick={() => shareBlog("whatsapp", blogDetail)}
                        className="flex items-center gap-2 rounded-xl bg-[#21b457] px-6 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:scale-105 active:scale-95"
                      >
                        <Share2 className="h-4 w-4" />
                        WhatsApp
                      </button>
                      <button
                        onClick={() => shareBlog("facebook", blogDetail)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1877F2] text-white shadow-sm transition-all hover:scale-110 hover:shadow-md"
                        title="Share on Facebook"
                      >
                        <Facebook className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => shareBlog("instagram", blogDetail)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-[#FFB000] via-[#FD1D1D] to-[#833AB4] text-white shadow-sm transition-all hover:scale-110 hover:shadow-md"
                        title="Share on Instagram"
                      >
                        <Instagram className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => shareBlog("linkedin", blogDetail)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0A66C2] text-white shadow-sm transition-all hover:scale-110 hover:shadow-md"
                        title="Share on LinkedIn"
                      >
                        <Linkedin className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => shareBlog("copy", blogDetail)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#6B7280] text-white shadow-sm transition-all hover:scale-110 hover:shadow-md"
                        title="Copy Link"
                      >
                        <Link2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
